import { Box, Button, Flex, HStack, IconButton, SimpleGrid, Stack, Stat, StatHelpText, StatLabel, StatNumber, Text } from '@chakra-ui/react';
import { DataTable, DeleteDialog, EmptyTable, LoadingTable } from 'components';
import { Link as DefaultLink, useNavigate } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { companyCustomerType, customerFormTypes } from 'types';
import { deleteCustomer, fetchCustomers } from 'store/actions';
import { getPreviousMonth, getPreviousWeek } from 'utils';
import { useAppDispatch, useAppSelector } from 'hooks';

import { AppDispatch } from 'store';
import CountUp from 'react-countup';
import { GrRefresh } from 'react-icons/gr';
import { HiOutlineArrowTrendingUp } from 'react-icons/hi2';
import { ToastContainer } from 'react-toastify';
import { createColumnHelper } from '@tanstack/react-table';
import moment from 'moment';

export const ViewCustomers = () => {

    const [isOpen, setDeleteDialog] = useState<boolean>(false);

    const { customers, is_loading_customers } = useAppSelector(state => state.customersReducer);

    const navigate = useNavigate();

    const dispatch: AppDispatch = useAppDispatch();

    const weekOne = getPreviousWeek(1);
    const weekTwo = getPreviousWeek(2);

    const monthOne = getPreviousMonth(1);
    const monthTwo = getPreviousMonth(2);

    const getPreviousWeekCustomers = () => customers.filter((customer: companyCustomerType) => moment(customer.createdAt).format('YYYY-MM-DD') >= weekTwo && moment(customer.createdAt).format('YYYY-MM-DD') < weekOne).length;
    const getPreviousMonthCustomers = () => customers.filter((customer: companyCustomerType) => moment(customer.createdAt).format('YYYY-MM-DD') >= monthTwo && moment(customer.createdAt).format('YYYY-MM-DD') <= monthOne).length;

    const thisWeekCustomers = () => customers.filter((customer: companyCustomerType) => moment(customer.createdAt).format('YYYY-MM-DD') >= weekOne).length;
    const thisMonthCustomers = () => customers.filter((customer: companyCustomerType) => moment(customer.createdAt).format('YYYY-MM-DD') >= monthOne).length;

    // const getMonthPercentage = () => {

    //     let percentage = 0;

    //     if (thisMonthCustomers() > getPreviousMonthCustomers()) {
    //         percentage = (thisMonthCustomers() / getPreviousMonthCustomers()) * 100;
    //     } else if (thisMonthCustomers() === 0) {
    //         percentage = 0;
    //     } else {
    //         percentage = (getPreviousMonthCustomers() / thisMonthCustomers()) * 100;
    //     }

    //     return percentage;
    // };

    const data: customerFormTypes[] = customers;

    const columnHelper = createColumnHelper<customerFormTypes>();

    const columns = [
        columnHelper.accessor('customer_id', {
            cell: (info) => info.getValue() ?? '-',
            header: 'Customer ID'
        }),
        columnHelper.accessor('first_name', {
            cell: (info) => info.getValue() ?? '-',
            header: 'First Name'
        }),
        columnHelper.accessor('last_name', {
            cell: (info) => info.getValue() ?? '-',
            header: 'Last Name'
        }),
        columnHelper.accessor('email', {
            cell: (info) => info.getValue() ?? '-',
            header: 'Email'
        }),
        columnHelper.accessor('phone', {
            cell: (info) => info.getValue() ?? '-',
            header: 'Phone',
        }),
        columnHelper.accessor('address', {
            cell: (info) => info.getValue() ?? '-',
            header: 'Address',
        }),
        columnHelper.accessor('', {
            cell: ({ row }) => {
                const document_id: string | undefined = row?.original?._id;
                const customer_id: string | undefined = row?.original?.customer_id;
                return (
                    <>
                        <HStack>
                            <Button colorScheme={'teal'} variant={'outline'} size='xs' onClick={() => navigate(`/customers/${customer_id}/edit`)}>
                                Edit
                            </Button>
                            <Button colorScheme={'red'} variant={'outline'} size='xs' onClick={() => setDeleteDialog(!isOpen)}>
                                {/* {on_vehicle_delete ? '...' : 'Delete'} */} Delete
                            </Button>
                        </HStack>
                        <DeleteDialog
                            id={`${document_id}`}
                            item='Customer'
                            isOpen={isOpen}
                            onDelete={() => handleDeleteCustomer(`${document_id}`)}
                            onClose={() => setDeleteDialog(!isOpen)}
                        />
                    </>
                );
            },
            header: 'Actions',
        })
    ];

    const handleRefresh = () => {
        dispatch(fetchCustomers());
    };

    const handleDeleteCustomer = (id: string) => {
        dispatch(deleteCustomer(id));
        setDeleteDialog(!isOpen);
    };

    useEffect(() => {
        dispatch(fetchCustomers());
    }, []);

    return (
        <>
            <Flex
                minH={'-webkit-fill-available'}
                align={'center'}
                justify={'center'}
                pt={2}
                pb={10}
            >
                <Stack>
                    <Stack mb={4}>
                        <SimpleGrid columns={[1, null, 3]} spacing={6}>
                            <Stat bgColor={'white'} px={4} py={3} borderRadius={6}>
                                <StatLabel textTransform={'uppercase'} color={'gray.500'} fontSize={'xs'} fontWeight={'medium'}>All customers</StatLabel>
                                <HStack>
                                    <CountUp start={0} end={customers.length || 0} duration={2}>
                                        {
                                            ({ countUpRef }) => (
                                                <StatNumber fontSize={'4xl'}><span ref={countUpRef}>{customers.length || 0}</span></StatNumber>
                                            )
                                        }
                                    </CountUp>
                                    <HiOutlineArrowTrendingUp color='#4caf50' size={24} fontWeight={700} />
                                </HStack>
                                <StatHelpText color={'gray.500'} fontSize={'xs'}>All company customers</StatHelpText>
                            </Stat>
                            <Stat bgColor={'white'} px={4} py={3} borderRadius={6}>
                                <StatLabel textTransform={'uppercase'} color={'gray.500'} fontSize={'xs'} fontWeight={'medium'}>This week</StatLabel>
                                <HStack>
                                    <CountUp start={0} end={thisWeekCustomers() || 0} duration={2}>
                                        {
                                            ({ countUpRef }) => (
                                                <StatNumber fontSize={'4xl'}><span ref={countUpRef}>{thisWeekCustomers() || 0}</span></StatNumber>
                                            )
                                        }
                                    </CountUp>
                                    <HiOutlineArrowTrendingUp color='#4caf50' size={24} fontWeight={700} />
                                    <Text fontSize={'sm'} fontWeight={'semibold'} color={'green.500'}>{thisWeekCustomers() >= getPreviousWeekCustomers() ? '+' : '-'}{Math.abs(getPreviousWeekCustomers() - thisWeekCustomers())}</Text>
                                </HStack>
                                <StatHelpText color={'gray.500'} fontSize={'xs'}>Current week company customers</StatHelpText>
                            </Stat>
                            <Stat bgColor={'white'} px={4} py={3} borderRadius={6}>
                                <StatLabel textTransform={'uppercase'} color={'gray.500'} fontSize={'xs'} fontWeight={'medium'}>This month</StatLabel>
                                <HStack>
                                    <CountUp start={0} end={thisMonthCustomers() || 0} duration={2}>
                                        {
                                            ({ countUpRef }) => (
                                                <StatNumber fontSize={'4xl'}><span ref={countUpRef}>{thisMonthCustomers() || 0}</span></StatNumber>
                                            )
                                        }
                                    </CountUp>
                                    <HiOutlineArrowTrendingUp color='#4caf50' size={24} fontWeight={700} />
                                    <Text fontSize={'sm'} fontWeight={'semibold'} color={'green.500'}>{thisMonthCustomers() >= getPreviousMonthCustomers() ? '+' : '-'}{Math.abs(getPreviousMonthCustomers() - thisMonthCustomers())}</Text>
                                </HStack>
                                <StatHelpText color={'gray.500'} fontSize={'xs'}>Current month company customers</StatHelpText>
                            </Stat>
                        </SimpleGrid>
                    </Stack>
                    <Stack bg={'#FFFFFF'} p={4} borderRadius={6}>
                        <HStack justifyContent={'space-between'} mb={4} px={2}>
                            <Text fontSize={20} fontWeight={'medium'}>Customers Table</Text>
                            <HStack>
                                <IconButton aria-label='Refresh button' size={'sm'} icon={<GrRefresh className={false ? 'loading-refresh-icon' : ''} />} onClick={handleRefresh} />
                                <Button colorScheme={'yellow'} size='sm' as={DefaultLink} to='/customers/add'>
                                    Add Customer
                                </Button>
                            </HStack>
                        </HStack>
                        <SimpleGrid columns={[1, null, 1]} spacing='40px'>
                            <Box w='100%'>
                                <DataTable columns={columns} data={data} variant='striped' />
                                {is_loading_customers && !customers.length && <LoadingTable />}
                                {!customers.length && !is_loading_customers && <EmptyTable title='Oops!' description='Your company does not have customers.' />}
                            </Box>
                        </SimpleGrid>
                    </Stack>
                    <ToastContainer />
                </Stack>
            </Flex>
        </>
    );
};